import { jwtDecode } from "jwt-decode";

export const getAccessToken = () => localStorage.getItem("accessToken");
export const getRefreshToken = () => localStorage.getItem("refreshToken");

// Mirror the access token in a browser cookie in addition to
// localStorage. The cookie is what nginx-proxy reads when it
// makes the /api/v1/login/verify-grafana-access sub-request before
// proxying /monitoring/* to Grafana — localStorage isn't visible to
// the network layer. Cookie expiry mirrors the token's `exp` claim
// so a stale cookie can't outlive its JWT.
const ACCESS_TOKEN_COOKIE = "access_token";

const setAccessTokenCookie = (token) => {
  try {
    const { exp } = jwtDecode(token);
    const maxAge = Math.max(0, exp - Math.floor(Date.now() / 1000));
    const secure = window.location.protocol === "https:" ? " secure;" : "";
    document.cookie =
      `${ACCESS_TOKEN_COOKIE}=${token}; path=/;${secure} samesite=lax;` +
      ` max-age=${maxAge}`;
  } catch {
    // jwtDecode shouldn't fail on a server-issued token, but if it
    // does we still want localStorage to work — skip the cookie.
  }
};

const clearAccessTokenCookie = () => {
  document.cookie = `${ACCESS_TOKEN_COOKIE}=; path=/; max-age=0`;
};

export const setAccessToken = (token) => {
  localStorage.setItem("accessToken", token);
  setAccessTokenCookie(token);
};
export const setRefreshToken = (token) =>
  localStorage.setItem("refreshToken", token);

export const removeAccessToken = () => {
  localStorage.removeItem("accessToken");
  clearAccessTokenCookie();
};

export const isTokenExpired = (token) => {
  if (!token) return true;
  const { exp } = jwtDecode(token);
  if (!exp) return true;
  return Date.now() >= exp * 1000;
};

// One renewal at a time. A page that loads several queries at once gets them
// all refused when the access token expires; each renewal returns a new pair
// and revokes the previous one, so renewals started side by side revoked each
// other's tokens and the retries failed. Callers that arrive while a renewal
// is under way get that renewal's result.
//
// One at a time across the tabs of this browser as well. The tabs share
// localStorage and a refresh token renews once: two tabs renewing at the same
// moment had one refused, and its sign-out cleared the pair the other tab had
// just stored. A tab that waited for the lock finds the other tab's pair.
let renewalInProgress = null;
const RENEWAL_LOCK = "rf-sedra-session-renewal";

export const refreshToken = () => {
  if (!renewalInProgress) {
    const seen = getRefreshToken();
    renewalInProgress = acrossTabs(() => renewUnlessRenewed(seen)).finally(
      () => {
        renewalInProgress = null;
      }
    );
  }
  return renewalInProgress;
};

const acrossTabs = (task) =>
  typeof navigator !== "undefined" && navigator.locks?.request
    ? navigator.locks.request(RENEWAL_LOCK, task)
    : task();

const renewUnlessRenewed = (seen) => {
  const current = getRefreshToken();
  if (current && current !== seen && getAccessToken()) {
    return Promise.resolve(getAccessToken());
  }
  return renew();
};

const renew = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken || isTokenExpired(refreshToken)) {
    throw new Error("Refresh token expired");
  }

  const response = await fetch(
    `${import.meta.env.VITE_API_BASE_URL}/login/new_access_token`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    }
  );

  if (!response.ok) {
    // A refresh token renews once. Refused because another tab of this browser
    // renewed first: that tab has stored the new pair, which this one shares.
    if (getRefreshToken() !== refreshToken && getAccessToken()) {
      return getAccessToken();
    }
    throw new Error("Failed to refresh token");
  }

  const data = await response.json();
  // A renewal rotates the pair: the backend revokes the refresh token sent
  // here (generate_auth_token), so only the one returned is accepted by the
  // next renewal. Keeping the old one signed users out at the second renewal.
  const { access_token, refresh_token } = data.data;

  setAccessToken(access_token);
  setRefreshToken(refresh_token);

  return access_token;
};
